import { Conversation, Message } from 'src/services/whatsapp'
import { MessageBubble } from './message-bubble'
import { Spinner } from '../spinner'
import { useEffect, useRef } from 'react'
import { format } from 'date-fns'
import { Link } from 'react-router'
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'
import { FiCalendar } from 'react-icons/fi'

interface ChatWindowProps {
  conversation: Conversation | null
  messages: Message[]
  isLoading?: boolean
  isError?: boolean
  error?: Error | null
  onMarkSeen?: () => void
}

export const ChatWindow = ({
  conversation,
  messages,
  isLoading = false,
  isError = false,
  error = null,
  onMarkSeen,
}: ChatWindowProps) => {
  const { t } = useTranslation()
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const messagesContainerRef = useRef<HTMLDivElement>(null)

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Keep the raw load failure for debugging; the UI shows plain copy only.
  useEffect(() => {
    if (isError && error) console.error('Failed to load conversation messages', error)
  }, [isError, error])

  // Mark as seen when conversation is selected
  useEffect(() => {
    if (conversation && conversation.unreadClientCount > 0) {
      onMarkSeen?.()
    }
  }, [conversation, onMarkSeen])

  const formatDateHeader = (dateString: string | null | undefined) => {
    if (!dateString) return ''
    try {
      const date = new Date(dateString)
      const today = new Date()
      const yesterday = new Date(today)
      yesterday.setDate(yesterday.getDate() - 1)

      if (format(date, 'yyyy-MM-dd') === format(today, 'yyyy-MM-dd')) {
        return t('chat.today')
      }
      if (format(date, 'yyyy-MM-dd') === format(yesterday, 'yyyy-MM-dd')) {
        return t('chat.yesterday')
      }
      return format(date, 'd MMMM yyyy')
    } catch {
      return ''
    }
  }

  const groupedMessages = messages.reduce(
    (acc, message) => {
      if (!message.created) return acc
      const dateKey = format(new Date(message.created), 'yyyy-MM-dd')
      if (!acc[dateKey]) {
        acc[dateKey] = []
      }
      acc[dateKey].push(message)
      return acc
    },
    {} as Record<string, Message[]>,
  )

  if (!conversation) {
    return (
      <div className="flex h-full items-center justify-center bg-surface-hover">
        <div className="text-center">
          <p className="text-lg font-semibold text-text-placeholder">
            {t('chat.selectConversation')}
          </p>
          <p className="mt-2 text-sm text-text-tertiary">{t('chat.selectConversationHint')}</p>
        </div>
      </div>
    )
  }

  const guestName = conversation.guestRef
    ? `${conversation.guestRef.firstName || ''} ${conversation.guestRef.lastName || ''}`.trim()
    : ''
  // Prefer a linked contact's name, then a guest name, then the raw number.
  const displayName =
    conversation.contactRef?.displayName || guestName || conversation.contactNumber

  return (
    <div className="flex h-full flex-col bg-surface">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border bg-surface px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-200 text-primary-800">
            <span className="text-sm font-semibold">{displayName.charAt(0).toUpperCase()}</span>
          </div>
          <div>
            <h2 className="font-semibold text-text-primary">{displayName}</h2>
            <p className="text-sm text-text-placeholder">{conversation.contactNumber}</p>
          </div>
        </div>
      </div>

      {/* Reservation banner */}
      {conversation.reservationRef && (
        <div className="border-b border-accent-200 bg-accent-50 px-4 py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-accent-800">
                <FiCalendar className="inline h-4 w-4" /> {t('chat.linkedReservation')}{' '}
                {conversation.reservationRef.providerConfirmationCode ||
                  conversation.reservationRef.id.substring(0, 8)}
              </span>
              {conversation.reservationRef.checkinDate && (
                <span className="text-xs text-accent-600">
                  {format(new Date(conversation.reservationRef.checkinDate), 'MMM d')} -{' '}
                  {conversation.reservationRef.checkoutDate
                    ? format(new Date(conversation.reservationRef.checkoutDate), 'MMM d')
                    : 'TBD'}
                </span>
              )}
            </div>
            {conversation.reservation && (
              <Link
                to={`/reservations/${conversation.reservation}`}
                className="text-xs font-medium text-accent-600 hover:text-accent-800"
              >
                {t('chat.viewDetails')}
              </Link>
            )}
          </div>
        </div>
      )}

      {/* Messages area */}
      <div
        ref={messagesContainerRef}
        className="flex min-h-0 flex-1 flex-col overflow-y-auto bg-surface-hover"
      >
        {isError && (
          <div className="flex items-center gap-2 border-b border-error-200 bg-error-50 px-4 py-3 text-error-800">
            <ExclamationTriangleIcon className="h-5 w-5 shrink-0" aria-hidden />
            <div>
              <p className="font-medium">{t('chat.failedToLoad')}</p>
              {/* Plain reassurance only — the raw error (status code, provider
                  string) is logged below, never shown. */}
              <p className="text-sm">{t('chat.somethingWentWrong')}</p>
            </div>
          </div>
        )}
        <div className="flex flex-1 flex-col-reverse p-4" style={{ minHeight: 'min-content' }}>
          <div ref={messagesEndRef} />
          {isLoading && messages.length === 0 ? (
            <div className="flex flex-1 items-center justify-center p-8">
              <Spinner size="md" />
            </div>
          ) : Object.keys(groupedMessages).length === 0 ? (
            <div className="flex min-h-[12rem] flex-1 items-center justify-center p-8 text-center">
              <div>
                <p className="text-text-placeholder">{t('chat.noMessages')}</p>
                <p className="mt-2 text-sm text-text-tertiary">{t('chat.noMessagesHint')}</p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {Object.entries(groupedMessages)
                .reverse()
                .map(([dateKey, dateMessages]) => (
                  <div key={dateKey}>
                    {/* Date header */}
                    <div className="my-4 flex items-center justify-center">
                      <span className="rounded-full bg-gray-200 px-3 py-1 text-xs font-medium text-text-secondary">
                        {formatDateHeader(dateMessages[0]?.created)}
                      </span>
                    </div>
                    {/* Messages for this date */}
                    <div className="space-y-2">
                      {dateMessages.map((message) => (
                        <MessageBubble key={message.id} message={message} />
                      ))}
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
